import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'

import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'

import { ESTIMATE_UNITS } from '@/checkout-instore-legacy/shipping/constants'
import {
  getShippingEstimateUnit,
  getShippingEstimateQuantity,
  getShippingEstimateQuantityInSeconds,
} from '@/checkout-instore-legacy/shipping/utils'
import Icon from '@/checkout-instore-legacy/components/Commons/Icons'
import COLORS from '@/checkout-instore-legacy/constants/Colors'
import { getCurrencyCode } from '@/checkout-instore-legacy/utils/PaymentUtils'

const getCells = (intl) => [
  {
    id: 'store',
    title: intl.formatMessage({ id: 'product.shippingAid.title.sla' }),
    cellRenderer: slaNameRenderer,
  },
  {
    id: 'shippingEstimate',
    title: intl.formatMessage({
      id: 'product.shippingAid.title.shippingEstimate',
    }),
    cellRenderer: shippingEstimateCellRenderer,
  },
  {
    id: 'shippingCost',
    title: intl.formatMessage({
      id: 'product.shippingAid.title.shippingCost',
    }),
    cellRenderer: shippingCostCellRendererWithIntl(intl),
  },
]

export const getJsonColumnsData = (intl, freigthTable) => {
  const cells = getCells(intl)
  return cells.filter((cell) => freigthTable[cell.id])
}

const slaNameRenderer = ({ data: { iconType, name } = {} }) => {
  const iconSize = 16

  return (
    <div data-testid="sla-name" className="flex flex-row items-center lh-copy">
      <div>
        <Icon type={iconType} size={iconSize} color={COLORS.ON_BASE} />
      </div>
      <div className="c-on-base pl3 ws-normal t-small t-body-ns">{name}</div>
    </div>
  )
}
slaNameRenderer.propTypes = {
  data: PropTypes.shape({
    iconType: PropTypes.string,
    name: PropTypes.string,
  }),
}

const shippingEstimateCellRenderer = ({ data: shippingEstimate }) => {
  if (isEmpty(shippingEstimate)) {
    return (
      <span className="c-on-base ws-normal t-small t-body-ns lh-copy">
        <FormattedMessage id="product.shippingAid.now" />
      </span>
    )
  }
  const unit = getShippingEstimateUnit(shippingEstimate)
  const quantity = getShippingEstimateQuantity(shippingEstimate)

  const intlId = getIntlIdFromEstimateUnit(unit)
  return intlId ? (
    <span
      className="c-on-base ws-normal t-small t-body-ns lh-copy"
      data-testid="shipping-estimate"
    >
      <FormattedMessage id={intlId} values={{ quantity }} />
    </span>
  ) : (
    // Fallback to display hour
    <span
      className="c-on-base ws-normal t-small t-body-ns"
      data-testid="shipping-estimate"
    >
      <FormattedMessage
        id="product.shippingAid.shipping.estimate.h"
        values={{
          quantity:
            getShippingEstimateQuantityInSeconds(shippingEstimate) / (60 * 60),
        }}
      />
    </span>
  )
}
shippingEstimateCellRenderer.propTypes = {
  data: PropTypes.shape({
    shippingEstimate: PropTypes.string,
  }),
}

const shippingCostCellRendererWithIntl = (intl) => {
  const shippingCostCellRenderer = ({ data: costValue }) => {
    if (!isNumber(costValue)) {
      return (
        <span className="c-on-base ws-normal t-small t-body-ns">
          <FormattedMessage id="product.shippingAid.free" />
        </span>
      )
    }
    const costFormatted = intl.formatNumber(costValue / 100, {
      style: 'currency',
      currency: getCurrencyCode(),
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
    })

    return (
      <span
        className="c-on-base ws-normal t-small t-body-ns"
        data-testid="shipping-cost"
      >
        <FormattedMessage
          id="product.shippingAid.shipping.cost"
          values={{ costValue, costFormatted }}
        />
      </span>
    )
  }
  shippingCostCellRenderer.propTypes = {
    data: PropTypes.shape({
      costValue: PropTypes.number,
    }),
  }
  return shippingCostCellRenderer
}

const getIntlIdFromEstimateUnit = (unit) => {
  const allowedUnits = [
    ESTIMATE_UNITS.MINUTES,
    ESTIMATE_UNITS.HOURS,
    ESTIMATE_UNITS.DAYS,
    ESTIMATE_UNITS.BUSINESS_DAYS,
  ]
  if (!allowedUnits.includes(unit)) {
    return
  }
  return `product.shippingAid.shipping.estimate.${unit}`
}
